import * as THREE from "three";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { WebGLAtlasData, WebGLStaticAtlasBuilder, IUVBounds } from "./webgl_static_atlas_bulder.js";

export class Vfx3DGlobalAtlas
{
  private static ENABLE_LOGGING: boolean = false;

  private static atlas_texture: THREE.CanvasTexture | null = null;
  private static atlas_data: WebGLAtlasData<ResourceID> | null = null;

  public static init(
    resources: ResourceID[],
    max_width: number,
    padding: number = 2
  ): void
  {
    if ( this.atlas_texture )
      return;

    // Extract images from ResourceManager
    const sprite_inputs: { id: ResourceID; image: HTMLImageElement | HTMLCanvasElement | ImageBitmap }[] = [];
    for ( const res_id of resources )
    {
      const texture = ResourceManager.getTexture( res_id );
      if ( texture && texture.image )
        sprite_inputs.push( { id: res_id, image: texture.image as any } );
      else
        console.warn( `[Vfx3DGlobalAtlas] Texture for ResourceID ${res_id} not loaded or missing image.` );
    }

    // Use WebGLStaticAtlasBuilder to pack layout and extrude edges
    try
    {
      this.atlas_data = WebGLStaticAtlasBuilder.buildAtlasData( sprite_inputs, {
        max_width,
        max_height: max_width,
        power_of_two: true,
        padding
      } );
    }
    catch ( err )
    {
      throw new Error(
        `[Vfx3DGlobalAtlas] Atlas overflow! Current VFX_3D_ATLAS_SIZE (${max_width}x${max_width} px) is too small. ` +
        `Details: ${( err as Error ).message}`
      );
    }

    // Create Three.js CanvasTexture from packed atlas image
    this.atlas_texture = new THREE.CanvasTexture( this.atlas_data.image as HTMLCanvasElement );
    this.atlas_texture.minFilter = THREE.LinearFilter;
    this.atlas_texture.magFilter = THREE.LinearFilter;
    this.atlas_texture.wrapS = THREE.ClampToEdgeWrapping;
    this.atlas_texture.wrapT = THREE.ClampToEdgeWrapping;

    if ( this.ENABLE_LOGGING )
      this.logAtlasStats( max_width );
  }

  public static logAtlasStats( _max_size?: number ): void
  {
    if ( !this.atlas_data )
      return;

    let sprites_area = 0;
    for ( const sprite of this.atlas_data.sprites.values() )
      sprites_area += sprite.w * sprite.h;

    const canvas_area = this.atlas_data.width * this.atlas_data.height;
    const free_area = canvas_area - sprites_area;
    const free_percent = ( ( free_area / canvas_area ) * 100 ).toFixed( 1 );
    const used_percent = ( ( sprites_area / canvas_area ) * 100 ).toFixed( 1 );
    const memory_mb = ( ( canvas_area * 4 ) / ( 1024 * 1024 ) ).toFixed( 1 );

    console.log(
      `[Vfx3DGlobalAtlas] Atlas compiled (${this.atlas_data.width}x${this.atlas_data.height} px, ${memory_mb} MB):\n` +
      `  • Sprites: ${this.atlas_data.sprites.size} pcs (${sprites_area.toLocaleString()} px², ${used_percent}% used)\n` +
      `  • Free Space: ${free_area.toLocaleString()} px² (${free_percent}% free)`
    );
  }

  public static getTexture(): THREE.CanvasTexture
  {
    if ( !this.atlas_texture )
      throw new Error( "[Vfx3DGlobalAtlas] Atlas not initialized. Call init() first." );

    return this.atlas_texture;
  }

  public static getAtlasData(): WebGLAtlasData<ResourceID>
  {
    if ( !this.atlas_data )
      throw new Error( "[Vfx3DGlobalAtlas] Atlas not initialized. Call init() first." );

    return this.atlas_data;
  }

  public static getUVRect( id: ResourceID ): IUVBounds
  {
    if ( !this.atlas_data )
      throw new Error( "[Vfx3DGlobalAtlas] Atlas not initialized. Call init() first." );

    const sprite = this.atlas_data.sprites.get( id );
    if ( !sprite )
      throw new Error( `[Vfx3DGlobalAtlas] Sprite not found in atlas: ${ResourceID[id] ?? id} (${id})` );
    
    // Three.js UV coordinates (V coordinate is inverted relative to 2D Canvas Y)
    return {
      min_x: sprite.u0,
      min_y: 1.0 - sprite.v1,
      max_x: sprite.u1,
      max_y: 1.0 - sprite.v0
    };
  }

  public static getStats(): {
    width: number;
    height: number;
    sprites_count: number;
    memory_bytes: number;
    free_percent: number;
  } | null
  {
    if ( !this.atlas_data )
      return null;

    let sprites_area = 0;
    for ( const sprite of this.atlas_data.sprites.values() )
      sprites_area += sprite.w * sprite.h;

    const canvas_area = this.atlas_data.width * this.atlas_data.height;
    const free_percent = canvas_area > 0 ? ( ( canvas_area - sprites_area ) / canvas_area ) * 100 : 0;

    return {
      width: this.atlas_data.width,
      height: this.atlas_data.height,
      sprites_count: this.atlas_data.sprites.size,
      memory_bytes: canvas_area * 4,
      free_percent,
    };
  }

  public static dispose(): void
  {
    if ( this.atlas_texture )
    {
      this.atlas_texture.dispose();
      this.atlas_texture = null;
    }
    this.atlas_data = null;
  }
}
